/** Written fix steps for every known flag / warning shown on a record. `go` builds the "Take me there" window. */
const campus = (rid, tab, extra = {}) => (rid ? ["campus_residents", { title: "Campus Residents", focus: { residentId: rid, tab, ...extra } }] : null);

export const FIX_GUIDES = {
  paycheck_flagged: {
    title: "This paycheck is flagged",
    steps: [
      "Read the flag note on the paycheck. It says what staff found wrong.",
      "Open the paycheck in Campus Residents → Financial (Take me there does this when you're on another card).",
      "Press Edit and fix what the note describes: employer, pay dates or gross amount.",
      "Make the split add up to the gross. Use 'Put the rest in Cash in Hand' if needed, then Save.",
      "Press Approve to post it. If it shouldn't exist at all, press Void and add a short note.",
    ],
    go: (c) => campus(c.residentId, "financial", { paycheckId: c.paycheckId }),
  },
  paycheck_superseded: {
    title: "Paycheck week resolved — please review",
    steps: [
      "Two or more paychecks covered the same work week, so the most trusted one was kept: invoice-backed first, then self-logged / staff / statement, then old portal import.",
      "The others were set aside: still in the records, but no longer counted in balances, rent, case management, Line of Credit, savings or reports.",
      "Use Fix it with me to check the amounts. Press Looks right, or Keep it if a set-aside paycheck is real separate pay.",
    ],
    go: (c) => campus(c.residentId, "financial"),
  },
  paycheck_duplicate: {
    title: "This paycheck looks like a duplicate",
    steps: [
      "Open the resident's paychecks in Campus Residents → Financial (Take me there does this when you're on another card).",
      "Find the other paycheck with the same pay dates and the same gross.",
      "Decide which one is real (check the pay stub if you have it).",
      "On the extra copy, press Void and write 'duplicate of <date>'.",
      "On the real one, press Keep (or Approve) so it stays in the balances.",
    ],
    go: (c) => campus(c.residentId, "financial", { paycheckId: c.paycheckId }),
    guide: (c) => (c.paycheckId ? ["fix_duplicate", { title: "Fix it with me · duplicate paycheck", paycheckId: c.paycheckId }] : null),
  },
  gross_gap: {
    title: "Gross pay doesn't match the old portal",
    steps: [
      "Compare the two totals in the yellow box: live paychecks vs. old portal all-time.",
      "If live is LOWER, a paycheck is missing. Use Log paycheck in Paycheck Review to add it with the right pay dates.",
      "If live is HIGHER, look for a duplicate in the paychecks list and Void the extra copy.",
      "Check that rent and case management splits match the old portal too, and Edit any that don't.",
      "Refresh the tab. The gap should drop to $0.00 or to an amount you can explain in a note.",
    ],
    go: (c) => campus(c.residentId, "financial"),
  },
  invoice_overpaid: {
    title: "This invoice is overpaid",
    steps: [
      "Open the invoice's Payments list and find the payment that was entered twice.",
      "Reverse that payment and add a note such as 'duplicate payment, reversed'.",
      "Check that the balance due now shows $0.00 and the status shows Paid.",
    ],
  },
  clearinghouse_flagged: {
    title: "This clearinghouse item is flagged",
    steps: [
      "Read the Flag reason on the item.",
      "Fix the source record it points to (resident, amount or bucket), or collect the missing proof.",
      "Return to the item and Resolve or Approve it with a note saying what you fixed.",
    ],
  },
  agreement_unsigned: {
    title: "Required documents not signed",
    steps: [
      "Open the Documents card → Who's missing what, and pick Residents.",
      "Tap the ⏳ box for this resident and press Remind.",
      "Ask the resident to open PACT Life Pass → More → Your documents and sign each one (about a minute each).",
      "Once everything is signed, this flag clears on its own.",
    ],
    go: () => ["docs_center", { title: "Documents" }],
  },
  system_glitch: {
    title: "Possible system glitch (from a sit-down session)",
    steps: [
      "Read the reason and evidence: the AI found the records say one thing and the card says another.",
      "Don't change the resident's records to make the card go away.",
      "Send this to the platform developer to fix the card or data rule; it clears once it's resolved.",
    ],
  },
  no_pin: {
    title: "Resident has no PACT Life Pass PIN",
    steps: [
      "Open the resident in Campus Residents (Take me there) and, in the PACT Life Pass PIN panel, type their mobile number and a 6-digit PIN.",
      "Press Save PIN.",
      "Give the resident the PIN in person (never by text) and have them sign in once to check it works.",
    ],
    go: (c) => campus(c.residentId, "profile"),
  },
  selflog_unpaid_invoice: {
    title: "Self-log on unpaid invoice",
    steps: [
      "A paycheck was logged by hand for work that is on an employer invoice that isn't paid yet.",
      "Open the week on the resident's Financial page and press Undo early self-log.",
      "Check the preview, write the reason and press Confirm. The week then shows Awaiting employer until the invoice is marked paid.",
    ],
    go: (c) => campus(c.residentId, "financial", { undoWeek: c.week, undoPaycheckId: c.paycheckId }),
  },
  waterfall_break: {
    title: "Paycheck split skipped the payment order",
    steps: [
      "Paychecks pay PACT Fee first, then Rent, then the Line of Credit, then Cash in Hand.",
      "Open the paycheck in the resident's Financial page.",
      "Use Ledger correction to move money into PACT Fee and Rent first, or press Mark reviewed with the reason.",
    ],
    go: (c) => campus(c.residentId, "financial", { paycheckId: c.paycheckId }),
  },
  advance_missing_info: {
    title: "Resident advance is missing its category or note",
    steps: ["Every advance needs an item category and a receipt / note.", "Use Fix it with me to add them. The amount stays as posted."],
    go: (c) => campus(c.residentId, "financial"),
  },
  conduct_low: {
    title: "Conduct score is low",
    steps: [
      "Open the resident's NCNs and read the last few entries.",
      "Book a one-on-one with the resident in the Scheduler to go over them.",
      "Write a corrective plan in a case note (Campus Residents → Notes).",
      "Make sure any unsigned NCNs get the resident's signature.",
    ],
    go: (c) => campus(c.residentId, "notes"),
  },
  checkout_overdue: {
    title: "Resident is past their back-by time",
    steps: [
      "Call or text the resident to find out where they are.",
      "If they're back, mark the check-out as Returned.",
      "If you can't reach them within an hour, tell the house manager and log an incident.",
      "Add a case note with what happened (Campus Residents → Notes).",
    ],
    go: (c) => campus(c.residentId, "notes"),
  },
  obligation_overdue: {
    title: "Payment is overdue",
    steps: [
      "Open the resident's ledger and check what's still owed for this period.",
      "Talk with the resident about a payment date, and log any payment as soon as it comes in.",
      "If it can't be paid, a super admin can adjust it with a reason, or set up a loan.",
    ],
    go: (c) => campus(c.residentId, "financial"),
  },
  pillar_overdue: {
    title: "Homework is overdue",
    steps: [
      "Open the resident's pillar cell to see which homework is late.",
      "Check in with the resident. The lesson must be passed (4 out of 5) before the task opens.",
      "Extend the due date or reassign the homework if there's a good reason.",
    ],
    go: () => ["pillar_board", { title: "Pillar Board", queue: "overdue" }],
  },
  pillar_review: {
    title: "Homework is waiting for your review",
    steps: [
      "Open the pillar cell and read the resident's submission.",
      "Approve it, or send it back with a short note on what to fix.",
    ],
    go: () => ["pillar_board", { title: "Pillar Board", queue: "review" }],
  },
  touchpoint_due: {
    title: "Weekly review is due",
    steps: [
      "Meet with the resident (or call them) this week.",
      "Open their row and record the weekly review so the 7-day clock resets.",
    ],
  },
  schedule_conflict: {
    title: "Schedule conflict",
    steps: [
      "Read who is busy and when (listed in this box).",
      "Pick another time or another staff member, and check the green 'Everyone picked is free' line.",
      "Override only if the double-booking is on purpose, and say why in the notes.",
    ],
  },
};
